import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Modal } from "react-bootstrap"
import { Btn } from "components/atoms/btn"
import ChartColorPicker from "./ChartColorPicker"
import { map, get, includes, clone, pull, forEach, set } from "lodash"
import chartColors from "../logic/chartColor"
import { chartColorSetting } from "redux/ducks/Chart"
import { ChartColor } from "../logic/chartConfig"
import { colorReset } from "../logic/chartColor"
import { ChartFontSelector } from "components/atoms/chart/parts/ChatAddonPropety"

interface props {
  canvasId: string
  isVisible: boolean
  setVisible: Function
}

const BASE_COLOR = [
  {
    id: 0,
    displayTitle: "ラインチャート",
    key: "line",
    options: [
      {
        id: 0,
        displayTitle: "ライン",
        key: "Line_Line",
      },
    ],
  },
  {
    id: 1,
    displayTitle: "折れ線チャート",
    key: "lineFill",
    options: [
      {
        id: 0,
        displayTitle: "ライン",
        key: "LineFill_Line",
      },
      {
        id: 1,
        displayTitle: "グラデーション(上)",
        key: "LineFill_Top",
      },
      {
        id: 2,
        displayTitle: "グラデーション(下)",
        key: "LineFill_Bottom",
      },
    ],
  },
  {
    id: 2,
    displayTitle: "スクロールバー",
    key: "scrollbar",
    options: [
      {
        id: 0,
        displayTitle: "背景塗りつぶし",
        key: "Scroll_Back",
      },
      {
        id: 1,
        displayTitle: "表示範囲暗",
        key: "Scroll_ViewRange",
      },
      {
        id: 2,
        displayTitle: "表示範囲明",
        key: "Scroll_ViewRangeLight",
      },
      {
        id: 3,
        displayTitle: "データ塗りつぶし",
        key: "Scroll_Data",
      },
      {
        id: 4,
        displayTitle: "データライン",
        key: "Scroll_DataLine",
      },
      {
        id: 5,
        displayTitle: "ハンドルマーク",
        key: "Scroll_HandleMark",
      },
      {
        id: 6,
        displayTitle: "ハンドルサイドノブ",
        key: "Scroll_HandleBorder",
      },
    ],
  },
  {
    id: 3,
    displayTitle: "ローソク",
    key: "candle",
    options: [
      {
        id: 0,
        displayTitle: "ローソク陽線",
        key: "Candle_UpArea",
      },
      {
        id: 1,
        displayTitle: "ローソク陽線枠",
        key: "Candle_UpFrame",
      },
      {
        id: 2,
        displayTitle: "ローソク陰線",
        key: "Candle_DownArea",
      },
      {
        id: 3,
        displayTitle: "ローソク陰線枠",
        key: "Candle_DownFrame",
      },
    ],
  },
  {
    id: 4,
    displayTitle: "チャート",
    key: "area",
    options: [
      {
        id: 0,
        displayTitle: "エリア外塗りつぶし",
        key: "ChartFill",
      },
      {
        id: 1,
        displayTitle: "エリア内塗りつぶし",
        key: "AreaFill",
      },
      {
        id: 2,
        displayTitle: "チャート枠線",
        key: "AreaDraw",
      },
      {
        id: 3,
        displayTitle: "グリッド",
        key: "Grid",
      },
      {
        id: 4,
        displayTitle: "サブチャートラベル",
        key: "SubChartLabel",
      },
      {
        id: 5,
        displayTitle: "X軸、Y軸のラベル表示",
        key: "Label",
      },
      {
        id: 6,
        displayTitle: "スケールライン",
        key: "CK_ScaleLine",
      },
      {
        id: 7,
        displayTitle: "クロスカーソル(ライン)",
        key: "CrossCursolLine",
      },
      {
        id: 8,
        displayTitle: "クロスカーソル(文字)",
        key: "CrossCursolLblDraw",
      },
      {
        id: 9,
        displayTitle: "クロスカーソル(ラベル)",
        key: "CrossCursolLblFill",
      },
    ],
  },
  {
    id: 5,
    displayTitle: "比較チャート",
    key: "compChart",
    options: [
      {
        id: 0,
        displayTitle: "対比銘柄1",
        key: "subBrand1",
      },
      {
        id: 1,
        displayTitle: "対比銘柄2",
        key: "subBrand2",
      },
    ],
  },
]

const TOOL_COLOR = [
  {
    id: 0,
    displayTitle: "トレンドライン",
    key: "trendLine",
    options: [
      {
        id: 0,
        displayTitle: "トレンドライン",
        key: "TrendLine",
        font: true,
      },
      {
        id: 1,
        displayTitle: "操作対象",
        key: "CK_HANDLINGDRAINGITEM",
      },
    ],
  },
  {
    id: 1,
    displayTitle: "ルーペ",
    key: "loupe",
    options: [
      {
        id: 0,
        displayTitle: "範囲選択ライン",
        key: "CK_LOUPEFRAME",
      },
    ],
  },
  {
    id: 2,
    displayTitle: "操作時",
    key: "control",
    options: [
      {
        id: 0,
        displayTitle: "XY軸操作時のマーク",
        key: "CK_INNERAREAHANDLE",
      },
    ],
  },
  {
    id: 3,
    displayTitle: "ポイント&フィギュア",
    key: "PandF",
    options: [
      {
        id: 0,
        displayTitle: "Down",
        key: "PAndF_Down",
      },
      {
        id: 1,
        displayTitle: "Up",
        key: "PAndF_Up",
      },
    ],
  },
  {
    id: 4,
    displayTitle: "フィボナッチ",
    key: "Fibo",
    options: [
      {
        id: 0,
        displayTitle: "リトレースメント",
        key: "FiboReTracement",
        font: true,
      },
      {
        id: 1,
        displayTitle: "エクスパンション",
        key: "FiboExpansion",
        font: true,
      },
      {
        id: 2,
        displayTitle: "タイムゾーン",
        key: "FiboTimeZone",
        font: true,
      },
      {
        id: 3,
        displayTitle: "ファン",
        key: "FiboFan",
        font: true,
      },
      {
        id: 4,
        displayTitle: "アーク",
        key: "FiboArc",
        font: true,
      },
    ],
  },
  {
    id: 5,
    displayTitle: "ギャン",
    key: "Gann",
    options: [
      {
        id: 0,
        displayTitle: "ファン",
        key: "GannFan",
        font: true,
      },
      {
        id: 1,
        displayTitle: "グリッド",
        key: "GannGrid",
        font: true,
      },
    ],
  },
  {
    id: 6,
    displayTitle: "窓",
    key: "Mado",
    options: [
      {
        id: 0,
        displayTitle: "騰窓",
        key: "MadoBackGroundUp",
      },
      {
        id: 1,
        displayTitle: "落窓",
        key: "MadoBackGroundDown",
      },
      {
        id: 2,
        displayTitle: "窓範囲",
        key: "MadoValue",
      },
    ],
  },
]

const ChartColorSettingModal = React.memo((props: props) => {
  const { canvasId, isVisible, setVisible } = props
  const colorSettingSelector = (state: any) => state.chart.colorSetting
  const plotTypeSelector = (state: any) => state.chart[canvasId].plotType
  const providerSelector = (state: any) => state.chart[props.canvasId].providers
  const colorSetting = useSelector(colorSettingSelector)
  const plotType = useSelector(plotTypeSelector)
  const providers = useSelector(providerSelector)
  const [_provider, _controller, _procs] = providers

  const [componentType, setComponentType] = useState(0)

  const components = [
    {
      id: 0,
      key: "baseColor",
      propeties: BASE_COLOR,
    },
    {
      id: 1,
      key: "toolColor",
      propeties: TOOL_COLOR,
    },
    {
      id: 2,
      key: "reset",
      propeties: null,
    },
  ]

  const onHide = () => {
    setVisible(false)
  }

  const viewComponent = (id: number) => {
    const component =
      id === 2 ? (
        <ChartColorResetConfirmation
          setVisible={setComponentType}
          setParentVisible={setVisible}
        />
      ) : (
        <ChartColorAcordeon
          propeties={components[id]}
          componentType={componentType}
          canvasId={canvasId}
        />
      )
    return component
  }

  useEffect(() => {
    chartColors.setColorPattern(plotType, colorSetting, _controller)
  }, [colorSetting])

  return (
    <Modal show={isVisible} dialogClassName="chart-color-setting-modal">
      <Modal.Header>
        <div className="header-title">カラー設定</div>
        <div className="header-nav-icon-col">
          <div className="header-icon ripple" onClick={() => onHide()}>
            <i className="material-icons">close</i>
          </div>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="modal-body-content">
          <div className="chart-color-setting-buttoms" id="color-picker-point">
            <div className="btn-multi-control">
              <label className="btn btn-outline shaped">
                <input
                  type="checkbox"
                  className="input-multi-control"
                  onChange={() => setComponentType(0)}
                  checked={componentType === 0}
                />
                <span className="ripple">基本</span>
              </label>
            </div>
            <div className="btn-multi-control">
              <label className="btn btn-outline shaped">
                <input
                  type="checkbox"
                  className="input-multi-control"
                  onChange={() => setComponentType(1)}
                  checked={componentType === 1}
                />
                <span className="ripple">ツール</span>
              </label>
            </div>
            <div className="btn-multi-control">
              <label className="btn btn-outline shaped">
                <input
                  type="checkbox"
                  className="input-multi-control"
                  onChange={() => setComponentType(2)}
                  checked={componentType === 2}
                />
                <span className="ripple">初期化</span>
              </label>
            </div>
          </div>
          <div className="chart-color-setting-content">
            {viewComponent(componentType)}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="modal-bottom-btn-row">
          <div
            className="btn btn-outline ripple"
            data-izimodal-close
            onClick={() => onHide()}
          >
            閉じる
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  )
})

const ChartColorAcordeon = React.memo(
  (props: { propeties: object; componentType: number; canvasId: string }) => {
    const { propeties, componentType } = props
    const [visibleList, setVisibleList] = useState([])

    const _setVisibleList = (id: number) => {
      let _visibleList = clone(visibleList)
      if (includes(_visibleList, id)) {
        pull(_visibleList, id)
      } else {
        _visibleList.push(id)
      }
      setVisibleList(_visibleList)
    }

    useEffect(() => {
      setVisibleList([])
    }, [componentType])

    return (
      <div>
        {map(get(propeties, "propeties", {}), items => {
          return (
            <div key={items.id}>
              <div className="chart-color-chart-setting-header">
                <Btn
                  onClick={() => _setVisibleList(items.id)}
                  name={items.displayTitle}
                />
                <i
                  className="material-icons"
                  onClick={() => _setVisibleList(items.id)}
                >
                  {includes(visibleList, items.id)
                    ? "keyboard_arrow_up"
                    : "keyboard_arrow_down"}
                </i>
              </div>
              {includes(visibleList, items.id) && (
                <ChartColorPropety
                  options={items.options}
                  type={get(propeties, "key")}
                  targetGroup={items.key}
                  canvasId={props.canvasId}
                />
              )}
            </div>
          )
        })}
      </div>
    )
  }
)

const ChartColorPropety = React.memo(
  (props: {
    options: object
    type: string
    targetGroup: string
    canvasId: string
  }) => {
    const { options, type, targetGroup } = props
    return (
      <div className="chart-color-setting-options">
        {map(options, items => {
          return (
            <div
              key={get(items, "id")}
              className="chart-color-setting-options-item"
            >
              <div>{get(items, "displayTitle")}</div>
              <div className="chart-color-setting-options-control">
                <ChartColorPicker
                  type={type}
                  targetGroup={targetGroup}
                  target={get(items, "key")}
                  parentId={"color-picker-point"}
                />
                {get(items, "font") && (
                  <ChartFontSelector
                    chartType={type}
                    targetGroup={targetGroup}
                    target={get(items, "key")}
                    canvasId={props.canvasId}
                  />
                )}
              </div>
            </div>
          )
        })}
      </div>
    )
  }
)

const ChartColorResetConfirmation = React.memo(
  (props: { setVisible: Function; setParentVisible?: Function }) => {
    const { setVisible, setParentVisible } = props
    const colorSettingSelector = (state: any) => state.chart.colorSetting
    const colorSetting = useSelector(colorSettingSelector)
    const dispatch = useDispatch()

    const _colorReset = () => {
      const _colorSetting = clone(colorSetting)
      const newColors = new ChartColor().ColorSetting
      colorReset(_colorSetting, newColors)
      dispatch(chartColorSetting(colorReset(_colorSetting, newColors)))
    }

    const onHide = (okFlag = false) => {
      setVisible(0)
      // 親のモーダル（ColorSettingModal）も閉じる。その際に、reduxの反映よりも早く閉じると、親のモーダルで色の変更処理をしている為、チャートに反映されなくなる。
      // その為、setTimeOutで遅らせる。
      if (!okFlag) return
      if (setParentVisible)
        setTimeout(() => {
          setParentVisible(false)
        }, 50)
    }

    return (
      <div className="chart-color-reset-confirmation">
        <div className="chart-color-reset-confirmation-text">
          <p>チャートのカラー設定を初期化します。よろしいですか？</p>
        </div>
        <div className="chart-color-setting-buttoms chart-color-reset-confirmation-buttoms">
          <div
            className="btn btn-outline ripple"
            onClick={() => {
              _colorReset()
              onHide(true)
            }}
          >
            OK
          </div>
          <div className="btn btn-outline ripple" onClick={() => onHide()}>
            キャンセル
          </div>
        </div>
      </div>
    )
  }
)

export default ChartColorSettingModal
